import React, { useState } from 'react';
import XCircleIcon from '../icons/XCircleIcon';
import UsersIcon from '../icons/UsersIcon';

interface DineInCoversModalProps {
  tableName: string;
  maxSeats?: number;
  /** Pre-fill (e.g. reservation party size when adjusting). */
  initialCovers?: number;
  /** When set, copy explains this is an adjustment vs the booking. */
  bookedPartySize?: number;
  onConfirm: (covers: number) => void;
  onCancel: () => void;
}

const DineInCoversModal: React.FC<DineInCoversModalProps> = ({
  tableName,
  maxSeats,
  initialCovers,
  bookedPartySize,
  onConfirm,
  onCancel,
}) => {
  const safeMax = typeof maxSeats === 'number' && maxSeats > 0 ? maxSeats : 20;
  const start = Math.min(
    Math.max(1, initialCovers && initialCovers > 0 ? initialCovers : 2),
    safeMax,
  );
  const [covers, setCovers] = useState<number>(start);
  const isAdjust = typeof bookedPartySize === 'number' && bookedPartySize > 0;

  const handleChange = (value: string) => {
    const n = parseInt(value, 10);
    if (isNaN(n)) {
      setCovers(1);
      return;
    }
    if (n < 1) {
      setCovers(1);
      return;
    }
    if (n > safeMax) {
      setCovers(safeMax);
      return;
    }
    setCovers(n);
  };

  const handleConfirm = () => {
    if (covers < 1) return;
    onConfirm(covers);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-pink-600" />
            {isAdjust ? 'Actual party size' : 'Seat new party'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-800"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {isAdjust ? (
            <p className="text-sm text-gray-700">
              Reservation for <span className="font-semibold">{tableName}</span> was{' '}
              <span className="font-semibold">{bookedPartySize}</span>. Change only if more (or fewer)
              guests arrived.
            </p>
          ) : (
            <p className="text-sm text-gray-700">
              How many customers are you seating at <span className="font-semibold">{tableName}</span>?
            </p>
          )}
          {typeof maxSeats === 'number' && maxSeats > 0 && (
            <p className="text-xs text-gray-500">
              Up to <span className="font-semibold">{maxSeats}</span> seats available for this table right now.
            </p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <button
              type="button"
              onClick={() => setCovers(prev => Math.max(1, prev - 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 text-lg font-bold text-gray-700 hover:bg-gray-100"
            >
              −
            </button>
            <input
              type="number"
              min={1}
              max={safeMax}
              value={covers}
              onChange={e => handleChange(e.target.value)}
              className="w-20 text-center px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-base"
            />
            <button
              type="button"
              onClick={() => setCovers(prev => Math.min(safeMax, prev + 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 text-lg font-bold text-gray-700 hover:bg-gray-100"
            >
              +
            </button>
          </div>
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-semibold text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            data-testid="dine-in-covers-confirm"
            className="px-4 py-2 text-sm font-semibold bg-pink-500 text-white rounded-lg hover:bg-pink-600"
          >
            {isAdjust ? 'Update party size' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DineInCoversModal;
